import React, { useState, useMemo, useEffect } from 'react';

// Helper function to get initials from student name
const getStudentInitials = (student) => {
  const firstName = student.first_name || '';
  const lastName = student.last_name || '';
  return `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;
};

// Helper function to generate consistent colors based on student name
const getInitialsColor = (student) => {
  // Use monochrome blue gradient like TakeAttendancePage
  return 'from-blue-400 to-blue-600';
};

export default function StudentTable({ students = [], onTakeAttendance, onRemoveStudent, onBulkRemoveStudents, onAddStudent }) {
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  
  // Bulk selection state
  const [selectedStudents, setSelectedStudents] = useState(new Set());
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Filter students based on search query
  const filteredStudents = useMemo(() => {
    if (!searchQuery.trim()) return students;
    
    const query = searchQuery.toLowerCase();
    return students.filter(student => 
      student.first_name?.toLowerCase().includes(query) ||
      student.last_name?.toLowerCase().includes(query) ||
      `${student.first_name} ${student.last_name}`.toLowerCase().includes(query) ||
      student.email?.toLowerCase().includes(query)
    );
  }, [students, searchQuery]);

  // Calculate pagination based on filtered students
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage);
  const startIndex = (currentPage - 1) * studentsPerPage;
  const endIndex = startIndex + studentsPerPage;
  const currentStudents = useMemo(() => 
    filteredStudents.slice(startIndex, endIndex),
    [filteredStudents, startIndex, endIndex]
  );

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Reset to first page when students array changes
  React.useEffect(() => {
    setCurrentPage(1);
    setSelectedStudents(new Set());
  }, [students]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Selection handlers
  const toggleStudentSelection = (studentId) => {
    const newSelected = new Set(selectedStudents);
    if (newSelected.has(studentId)) {
      newSelected.delete(studentId);
    } else {
      newSelected.add(studentId);
    }
    setSelectedStudents(newSelected);
  };

  const selectAllStudents = () => {
    const allStudentIds = filteredStudents.map(s => s.student_id || s.id);
    setSelectedStudents(new Set([...selectedStudents, ...allStudentIds]));
  };

  const deselectAllStudents = () => {
    const filteredStudentIds = filteredStudents.map(s => s.student_id || s.id);
    setSelectedStudents(prev => {
      const newSelection = new Set(prev);
      filteredStudentIds.forEach(id => newSelection.delete(id));
      return newSelection;
    });
  };

  const handleBulkRemove = () => {
    if (selectedStudents.size === 0) return;
    
    const studentsToRemove = students.filter(s => 
      selectedStudents.has(s.student_id || s.id)
    );
    
    if (onBulkRemoveStudents) {
      onBulkRemoveStudents(studentsToRemove);
      // Reset selection after opening modal
      setSelectedStudents(new Set());
    } else {
      // Fallback to old confirmation method if new prop not provided
      const studentNames = studentsToRemove.map(s => `${s.first_name} ${s.last_name}`).join(', ');
      
      if (window.confirm(`Are you sure you want to remove ${selectedStudents.size} student(s)?\n\n${studentNames}`)) {
        studentsToRemove.forEach(student => {
          if (onRemoveStudent) {
            onRemoveStudent(student);
          }
        });
        setSelectedStudents(new Set());
      }
    }
  };

  const areAllStudentsSelected = filteredStudents.length > 0 && 
    filteredStudents.every(s => selectedStudents.has(s.student_id || s.id));

  return (
    <div className="flex flex-col h-full">
      {/* Table Controls */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-4">
          {/* Search Input */}
          <div className="relative">
            <svg 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search students..."
              value={searchQuery}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-0 focus:border-gray-300"
            />
          </div>
          
          <div className="text-sm text-gray-600">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredStudents.length)} of {filteredStudents.length} students
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedStudents.size} selected
            </span>
            <button
              onClick={areAllStudentsSelected ? deselectAllStudents : selectAllStudents}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {areAllStudentsSelected ? 'Deselect All' : 'Select All'}
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Add Student button with user icon */}
          <button
            onClick={onAddStudent}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition"
            title="Add Student"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
            </svg>
          </button>
          
          {/* Trash bin button - always visible but greyed out when no selection */}
          <button
            onClick={handleBulkRemove}
            disabled={selectedStudents.size === 0}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition ${
              selectedStudents.size > 0
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
            title={selectedStudents.size > 0 ? `Remove ${selectedStudents.size} student(s)` : 'Select students to remove'}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            {selectedStudents.size > 0 && (
              <span>{selectedStudents.size}</span>
            )}
          </button>
        </div>
      </div>

      {/* Scrollable table area */}
      <div className="flex-1 overflow-x-auto min-h-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500 w-12">
                <input
                  type="checkbox"
                  checked={areAllStudentsSelected}
                  onChange={() => areAllStudentsSelected ? deselectAllStudents() : selectAllStudents()}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">Student Name</th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">First Name</th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">Last Name</th>
              <th className="py-2.5 px-4 text-left font-semibold text-slate-500">Email</th>
            </tr>
          </thead>
          <tbody>
            {currentStudents.map((stu, idx) => {
              const studentId = stu.student_id || stu.id;
              const isSelected = selectedStudents.has(studentId);
              
              return (
                <tr
                  key={idx}
                  className={`border-b last:border-b-0 cursor-pointer transition ${
                    isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-slate-50'
                  }`}
                  onClick={() => toggleStudentSelection(studentId)}
                >
                  <td className="py-3 px-4" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleStudentSelection(studentId)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center">
                      <div className={`h-8 w-8 rounded-full bg-gradient-to-br ${getInitialsColor(stu)} flex items-center justify-center mr-3`}>
                        <span className="text-xs font-medium text-white">
                          {getStudentInitials(stu)}
                        </span>
                      </div>
                      <div className="font-medium text-slate-800">
                        {stu.first_name} {stu.last_name}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-slate-700">
                      {stu.first_name || '-'}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-slate-700">
                      {stu.last_name || '-'}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="text-slate-700">
                      {stu.email || '-'}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => goToPage(1)}
              disabled={currentPage === 1}
              className="px-2 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            
            <button
              onClick={goToPrevious}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ← Previous
            </button>
            
            {/* Page numbers */}
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNumber}
                    onClick={() => goToPage(pageNumber)}
                    className={`px-2 py-1 text-sm border rounded transition ${
                      currentPage === pageNumber
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={goToNext}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next →
            </button>
            
            <button
              onClick={() => goToPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-2 py-1 text-sm border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Footer with Take Attendance button */}
      {onTakeAttendance && (
        <div className="flex justify-end mt-4">
          <button
            onClick={onTakeAttendance}
            className="bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold shadow hover:bg-blue-700 transition"
          >
            Take Attendance +
          </button>
        </div>
      )}
    </div>
  );
}
